import type { FC } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Content, FormatContent, useContent } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import { useInView } from "src/shared/hooks/useInView";
import {
  type CarouselId,
  CarouselInteractionType,
  CarouselItemType,
  sendCarouselInteractionUAE,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { usePrefersReducedMotion } from "src/website-apps/shared/components/responsive-carousel/usePrefersReducedMotion";
import { ArrowRightIcon } from "./ArrowRightIcon";
import { theaterPromoBanner } from "./TheaterPromoBanner.styles";

export type TheaterPromoBannerMotion =
  | { type: "video"; src: string; poster?: string | undefined }
  | { type: "image"; src: string; ariaLabel: string };

export interface TheaterPromoBannerShow {
  name: string;
  url: string;
  motion: TheaterPromoBannerMotion;
  // Optional taller-cropped hero used on phone, where the wide composition crops too aggressively.
  mobileMotion?: TheaterPromoBannerMotion | undefined;
  formattedFromPrice?: string | undefined;
}

interface Props {
  categoryTitle: string;
  show: TheaterPromoBannerShow;
  onCtaClick?: () => void;
  /** The featured show's entity id, reported as the CarouselInteraction `itemId`. */
  entityId: number;
  /** The page's category id, reported as the CarouselInteraction `categoryId` (distinguishes Broadway from Theater). */
  categoryId: number;
  /** The banner's arm-specific CarouselInteraction id. */
  carouselId: CarouselId;
  /**
   * v1 only: render the phone CTA as a liquid-glass pill over the hero (see the Figma). The pill
   * replaces the plain "Featured: …" line on phone; the line and the desktop CTA card are unchanged
   * from tablet up.
   */
  headerCta?: boolean | undefined;
}

const HeroMotion: FC<{
  motion: TheaterPromoBannerMotion;
  className: string | undefined;
  animatedClassName: string | undefined;
}> = ({ motion, className, animatedClassName }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  if (motion.type === "video") {
    return (
      <video
        className={className}
        data-testid="theater-promo-hero-video-id"
        src={motion.src}
        {...(motion.poster ? { poster: motion.poster } : {})}
        autoPlay={!prefersReducedMotion}
        muted
        playsInline
        aria-hidden="true"
        tabIndex={-1}
      />
    );
  }

  return (
    <img
      className={`${className ?? ""} ${animatedClassName ?? ""}`.trim()}
      data-testid="theater-promo-hero-image-id"
      src={motion.src}
      alt={motion.ariaLabel}
    />
  );
};

export const TheaterPromoBanner: FC<Props> = ({
  categoryTitle,
  show,
  onCtaClick,
  entityId,
  categoryId,
  carouselId,
  headerCta = false,
}) => {
  const styles = theaterPromoBanner({ cta: headerCta ? "pill" : "inline" });
  const findTicketsLabel = useContent("cid$#4434603#"); // "Find tickets"
  const appContext = useAppContext();
  const { env } = appContext;
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const motion = isMobile ? (show.mobileMotion ?? show.motion) : show.motion;

  // CRH-762 2b: the banner rides the shared CarouselInteraction UAE (as the Top Performers and
  // featured-show carousels do) so it needs no new event registration. The arm is attributed via
  // the participation join on anonymousId.
  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0.5,
    onChange: (inView) => {
      if (!inView) return;
      sendCarouselInteractionUAE(appContext, env, {
        carouselId,
        interactionType: CarouselInteractionType.InView,
        itemType: CarouselItemType.Category,
        categoryId,
        itemCount: 1,
      });
    },
  });

  const handleCtaClick = () => {
    sendCarouselInteractionUAE(appContext, env, {
      carouselId,
      interactionType: CarouselInteractionType.Click,
      itemType: CarouselItemType.Category,
      itemId: entityId,
      itemIndex: 0,
      categoryId,
      itemCount: 1,
    });
    onCtaClick?.();
  };

  return (
    <div className={styles.root} ref={ref}>
      <div className={styles.hero}>
        <HeroMotion motion={motion} className={styles.heroImage} animatedClassName={styles.heroImageAnimated} />
        <div className={styles.heroScrim} />
        <div className={styles.heroFooter}>
          <div className={styles.heroContent}>
            <span className={styles.title}>{categoryTitle}</span>
            <span className={styles.subtitle}>
              {/* "Featured: “The Book of Mormon” in NYC" — plain text today; rendered raw so a
                  translator can still add emphasis without a client change. */}
              <Content id="cid$#4434600#" raw />
            </span>
            {headerCta ? (
              // v1 phone CTA: the liquid-glass pill over the image. The whole pill is the link.
              <a className={styles.ctaPill} href={show.url} onClick={handleCtaClick}>
                <span className={styles.ctaPillText}>
                  <Content id="cid$#4434600#" raw />
                </span>
                <span className={styles.ctaPillArrow} aria-hidden="true">
                  <ArrowRightIcon size={16} />
                </span>
              </a>
            ) : null}
          </div>
          <a className={styles.ctaCard} href={show.url} onClick={handleCtaClick}>
            <span className={styles.ctaCopy}>
              {/* "See “{0}” in NYC" — {0} is the show name. */}
              <span className={styles.ctaKicker}>
                <FormatContent id="cid$#4434607#" params={show.name} raw />
              </span>
              {show.formattedFromPrice ? (
                // "From {0}" — {0} is the formatted price.
                <span className={styles.ctaPrice}>
                  <FormatContent id="cid$#4434602#" params={show.formattedFromPrice} />
                </span>
              ) : null}
            </span>
            {findTicketsLabel ? <span className={styles.ctaButton}>{findTicketsLabel}</span> : null}
          </a>
        </div>
      </div>
    </div>
  );
};
